import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
} from "@mui/material";
import { addDays, format, parse, isSameWeek, startOfDay } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import SaveIcon14pxSvg from "../../../assets/images/icons/SaveIcon14pxSvg";
import { addRosterItem } from "../../../axios/RosterItem/RosterItemApi";
import CustomAutocomplete from "../../../components/CustomAutocomplete";
import CustomDateTimePicker from "../../../components/CustomDateTimeRangePicker";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import { CustomDialog } from "../../../components/StyledComponents";
import { useLoading } from "../../../context/LoadingContext";
import { useSettings } from "../../../context/SettingsContext";
import CustomInput from "../../../components/CustomInput";
import CustomSelect from "../../../components/CustomSelect";
import { enUS } from "date-fns/locale";

const AddRosterItemsDialog = ({
  usersData,
  shiftsData,
  onClose,
  selectedItem,
  activeWeek,
  onRefresh,
  selectedRows,
  genaralSettingDateFormat,
  page,
}) => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const [show, hide] = useLoading();
  const [user, setUser] = useState("");
  const currentLang = i18n.language;
  const filteredShifts = shiftsData.filter(
    (shift) => shift.shiftName !== "DEFAULT",
  );
  const [selectedDaysByWeek, setSelectedDaysByWeek] = useState({});
  // const [checkedDays, setCheckedDays] = useState({});

  useEffect(() => {
    if (selectedItem?.userId) {
      const user = usersData.find(
        (user) => user.userId === selectedItem.userId,
      );
      if (user) {
        setFormData((prev) => ({
          ...prev,
          username: user.fullName,
        }));
      }
    } else if (selectedItem?.groupId) {
      setFormData((prev) => ({
        ...prev,
        username: selectedItem.groupName,
      }));
    }
  }, [selectedItem, usersData]);

  const mappedUsersData = useMemo(() => {
    return (usersData || []).map((u) => ({
      ...u,
      groupName:
        u.groupName?.trim().toLowerCase() === "all users"
          ? t("common.all-user")
          : u.groupName,
    }));
  }, [usersData, t]);

  const isAddDisabled = () => {
    const hasUser = formData.userId || selectedItem?.userId;
    const hasShift = formData.shift;
    const hasStartDate = formData.startDate;

    const isUserValid = hasUser && validationMessages.userId === "";
    const isShiftValid = hasShift && validationMessages.shift === "";
    const isStartDateValid =
      hasStartDate && validationMessages.startDate === "";

    return !(isUserValid && isShiftValid && isStartDateValid);
  };

  const initialFormData = {
    startDate: new Date(selectedRows.startDate),
    endDate: page
      ? new Date(selectedRows.startDate)
      : selectedRows?.endBy
        ? new Date(selectedRows.endBy)
        : new Date(selectedRows.startDate),
    shift: null,
    userId: null,
    day: "",
    week: null,
    rosterId: "",
    username: "",
    groupId: null,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [selectedValue, setSelectedValue] = useState({
    shift: null,
    userId: null,
    groupId: null,
  });

  const [isSelectError, setIsSelectError] = useState({
    shift: false,
    userId: false,
  });
  const [resetTrigger, setResetTrigger] = useState({
    shift: false,
    userId: false,
  });
  const [hasInteracted, setHasInteracted] = useState({
    shift: false,
    userId: false,
  });

  const [isSelectValid, setIsSelectValid] = useState(false);
  const [isInputValid, setIsInputValid] = useState(false);
  const [isError, setIsError] = useState({
    shift: null,
    userId: null,
    startDate: null,
    endDate: null,
  });

  const [validationMessages, setValidationMessages] = useState({
    shift: "",
    userId: "",
    startDate: "",
    endDate: "",
  });

  const rosterStartDate = selectedRows?.startDate
    ? new Date(selectedRows.startDate)
    : null;
  const rosterEndDate = selectedRows?.endBy
    ? new Date(selectedRows.endBy)
    : null;

  const toDay = (d) => startOfDay(new Date(d));
  const isOutsideRosterRange = (date) => {
    if (!date || isNaN(new Date(date).getTime())) return false;
    const day = toDay(date);
    if (rosterStartDate && day < toDay(rosterStartDate)) return true;
    if (rosterEndDate && day > toDay(rosterEndDate)) return true;
    return false;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => {
      let updatedData = {
        ...prevData,
        [name]: value,
      };

      // Guard: không cho vượt ra ngoài roster
      const clampToRoster = (d) => {
        if (!d) return d;
        if (rosterStartDate && d < rosterStartDate) return rosterStartDate;
        if (rosterEndDate && d > rosterEndDate) return rosterEndDate;
        return d;
      };

      if (name === "startDate") {
        const s = clampToRoster(new Date(value));
        // if (s.getTime() !== new Date(value).getTime()) {
        //   toast.warn(t("common.date-out-of-roster-range")); // thêm key i18n
        // }
        updatedData.startDate = s;
        // đảm bảo endDate không nhỏ hơn startDate và cũng không vượt roster
        if (endOfRepitition) {
          const end = updatedData.endDate
            ? new Date(updatedData.endDate)
            : null;
          if (!end || end < s) {
            updatedData.endDate = s;
          } else {
            updatedData.endDate = clampToRoster(end);
          }
        }
      }

      if (name === "endDate") {
        // clamp end trong khung roster
        const eDate = clampToRoster(new Date(value));
        // if (eDate.getTime() !== new Date(value).getTime()) {
        //   toast.warn(t("common.date-out-of-roster-range"));
        // }
        updatedData.endDate = eDate;
        const startDate = new Date(updatedData.startDate);
        const endDateA = new Date(eDate);

        if (endDateA <= startDate) {
          setValidationMessages((prevMessages) => ({
            ...prevMessages,
            endDate: "End date must after start date",
          }));
        } else {
          setValidationMessages((prevMessages) => ({
            ...prevMessages,
            endDate: "",
          }));
        }
      }

      return updatedData;
    });
  };

  const handleDataChange = (name, newData) => {
    if (name === "userId") {
      if (newData.groupId) {
        setFormData((prevData) => ({
          ...prevData,
          groupId: newData.groupId,
        }));
      } else {
        setFormData((prevData) => ({
          ...prevData,
          userId: newData.userId,
        }));
      }
    }
    if (newData) {
      setSelectedValue((prev) => ({
        ...prev,
        [name]: newData,
      }));

      setHasInteracted((prev) => ({
        ...prev,
        [name]: true,
      }));

      setFormData((prevData) => ({
        ...prevData,
        [name]: newData?.[name],
      }));
    } else {
      setSelectedValue((prev) => ({
        ...prev,
        [name]: newData,
      }));
      setFormData((prevData) => ({
        ...prevData,
        [name]: newData,
      }));
    }
  };

  useEffect(() => {
    if (hasInteracted.userId) {
      setValidationMessages((prev) => ({
        ...prev,
        userId: formData.userId !== null ? "" : "User is required",
      }));
    }
    if (hasInteracted.shift) {
      setValidationMessages((prev) => ({
        ...prev,
        shift: formData.shift !== null ? "" : "Shift is required",
      }));
    }
  }, [formData.userId, formData.shift, hasInteracted]);

  // useEffect(() => {
  //    if (hasInteracted.userId || hasInteracted.shift) {
  //       const isUserIdValid = formData.userId !== '' || formData.username !== '';
  //       const isShiftIdValid = formData.shift !== '' && validationMessages.shift === '';
  //       setIsSelectError((prev) => ({
  //          userId: !isUserIdValid,
  //          shift: !isShiftIdValid,
  //       })
  //    );
  //       setIsSelectValid(isUserIdValid && isShiftIdValid);
  //    }
  // }, [formData, validationMessages, hasInteracted]);

  useEffect(() => {
    const { endDate } = formData;
    const isEndDateValid = endDate && validationMessages.endDate === "";
    setIsError((prevError) => ({
      ...prevError,
      endDate: isEndDateValid ? null : true,
    }));
    if (isEndDateValid) {
      setIsInputValid(true);
    } else {
      setIsInputValid(false);
    }
  }, [formData.endDate, validationMessages.endDate]);

  const handleAdditionalEmployeesDataChange = (name, newData, index) => {
    const updatedEmployees = formData.additionalEmployees.map((employee, i) =>
      i === index
        ? {
          ...employee,
          employee: newData,
        }
        : employee,
    );
    setFormData((prevData) => ({
      ...prevData,
      [name]: updatedEmployees,
    }));
  };

  // const daysOfWeek = [t('common.monday'), t('common.tuesday'), t('common.wednesday'), t('common.thursday'), t('common.friday'), t('common.saturday'), t('common.sunday')];

  // const [checkedDays, setCheckedDays] = useState(() => {
  //    // Dynamically initialize based on selectedItem's day
  //    const initialCheckedDays = {
  //       Monday: false,
  //       Tuesday: false,
  //       Wednesday: false,
  //       Thursday: false,
  //       Friday: false,
  //       Saturday: false,
  //       Sunday: false,
  //    };

  //    if (selectedItem && selectedItem.day) {
  //       initialCheckedDays[selectedItem.day] = true;
  //    }

  //    return initialCheckedDays;
  // });

  // const handleCheckDays = (event, weekIndex) => {
  //    const day = event.target.value;
  //    const { value } = event.target;
  //    setCheckedDays({
  //       Monday: false,
  //       Tuesday: false,
  //       Wednesday: false,
  //       Thursday: false,
  //       Friday: false,
  //       Saturday: false,
  //       Sunday: false,
  //       [day]: event.target.checked,
  //    });

  //    // Update formData.day
  //    setFormData((prevData) => ({
  //       ...prevData,
  //       day: event.target.checked ? day : '',
  //    }));
  //    setSelectedDaysByWeek((prev) => ({
  //       ...prev,
  //       [weekIndex]: value, // chỉ giữ 1 ngày duy nhất
  //    }));
  // };
  // const [checkedDays, setCheckedDays] = useState(() => {
  //    const initial = {};
  //    if (selectedItem && selectedItem.day) {
  //       // ví dụ: chọn tuần đầu tiên
  //       initial[0] = selectedItem.day;
  //    }
  //    return initial;
  // });
  // const handleCheckDays = (event, weekIndex) => {
  //    const { value, checked } = event.target;

  //    setCheckedDays((prev) => ({
  //       ...prev,
  //       [weekIndex]: checked ? value : '', // tuần này chỉ chọn 1 ngày
  //    }));

  //    // Cập nhật formData nếu cần
  //    setFormData((prevData) => ({
  //       ...prevData,
  //       day: checked ? value : '',
  //    }));

  //    // Gộp dữ liệu chọn
  //    setSelectedDaysByWeek((prev) => ({
  //       ...prev,
  //       [weekIndex]: checked ? value : '',
  //    }));
  // };

  // const [checkedDays, setCheckedDays] = useState({
  //    Monday: false,
  //    Tuesday: false,
  //    Wednesday: false,
  //    Thursday: false,
  //    Friday: false,
  //    Saturday: false,
  //    Sunday: false,
  // });

  const handleCheckDays = (event) => {
    const { value: day, checked } = event.target;

    setCheckedDays((prev) => ({
      ...prev,
      [day]: checked,
    }));

    setFormData((prevData) => {
      const prevDays = Array.isArray(prevData.day) ? prevData.day : [];
      const newDays = checked
        ? [...prevDays, day]
        : prevDays.filter((d) => d !== day);

      return {
        ...prevData,
        day: newDays,
      };
    });
  };

  // useEffect(() => {
  //    setFormData((prevData) => ({
  //       ...prevData,
  //       startDate: selectedItem?.startDate ? selectedItem.startDate : new Date(),
  //       endDate: selectedItem?.startDate ? selectedItem.startDate : new Date(),
  //       day: selectedItem?.day || '',
  //       week: activeWeek + 1,
  //       rosterId: selectedItem?.rosterId,
  //    }));
  //    setCheckedDays({
  //       Monday: false,
  //       Tuesday: false,
  //       Wednesday: false,
  //       Thursday: false,
  //       Friday: false,
  //       Saturday: false,
  //       Sunday: false,
  //       [selectedItem?.day]: true,
  //    });
  // }, [selectedItem]);

  useEffect(() => {
    // const currentDayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });

    // const dayToCheck = selectedItem?.day || format(new Date(selectedRows.formatStartDate), genaralSettingDateFormat);
    // const dayToCheck = selectedItem?.day || format(new Date(selectedRows.startDate), 'EEEE');
    const dayToCheck =
      selectedItem?.day ||
      format(new Date(selectedRows.startDate), "EEEE", { locale: enUS });

    setFormData((prevData) => ({
      ...prevData,
      startDate: selectedItem?.startDate
        ? selectedItem.startDate
        : parse(
          selectedRows.formatStartDate,
          genaralSettingDateFormat,
          new Date(),
        ),
      endDate: page
        ? new Date(selectedItem.startDate)
        : selectedRows?.endBy
          ? new Date(selectedRows.endBy)
          : new Date(selectedRows.startDate),
      day: dayToCheck,
      week: activeWeek + 1,
      rosterId: selectedItem?.rosterId,
    }));

    setCheckedDays({
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
      Sunday: false,
      [dayToCheck]: true,
    });
  }, [selectedItem]);

  const [endStatus, setEndStatus] = useState(false);

  const handleChangeEndStatus = (event) => {
    setEndStatus(event.target.value === "true");
  };

  const [endOfRepitition, setEndOfRepitition] = useState(
    page ? true : selectedRows?.endBy ? true : false,
  );

  const handleChangeEndOfRepitition = (event) => {
    const isEnd = event.target.value === "true";

    setEndOfRepitition(isEnd);

    setFormData((prev) => {
      let updated = { ...prev };

      if (isEnd) {
        // 👉 nếu chưa có endDate thì auto set = hôm nay
        if (!prev.endDate) {
          updated.endDate = new Date();
        }
      } else {
        // Giữ ngày hiển thị khi tắt End by (submit vẫn gửi endDate = null)
        updated.endDate = prev.startDate ?? prev.endDate ?? new Date();
      }

      return updated;
    });
  };

  const handleCloseDialog = () => {
    setFormData(initialFormData);
    setValidationMessages({
      shift: "",
      userId: "",
      startDate: "",
      endDate: "",
    });
    setIsSelectError({ shift: false, userId: false });
    setHasInteracted({ shift: false, userId: false });
    setSelectedValue({ shift: null, userId: null });
    setResetTrigger((prev) => ({
      shift: !prev.shift,
      userId: !prev.userId,
    }));
    onClose();
  };
  const WEEK_ORDER = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const reduceToChecked = (daysArr) =>
    WEEK_ORDER.reduce((acc, d) => ({ ...acc, [d]: daysArr.includes(d) }), {});

  const weekdayFromDate = (d) =>
    d ? format(d, "EEEE", { locale: enUS }) : null;
  const [checkedDays, setCheckedDays] = useState(() => reduceToChecked([]));

  useEffect(() => {
    const start = formData?.startDate ? new Date(formData.startDate) : null;
    const end = formData?.endDate ? new Date(formData.endDate) : null;

    if (!start || isNaN(start)) return;

    if (!endOfRepitition) {
      const allDays = [...WEEK_ORDER];
      setCheckedDays(reduceToChecked(allDays));
      setFormData((prev) => ({ ...prev, day: allDays }));
      return;
    }

    if (!end || isNaN(end)) {
      const onlyStart = weekdayFromDate(start);
      const picked = onlyStart ? [onlyStart] : [];
      setCheckedDays(reduceToChecked(picked));
      setFormData((prev) => ({ ...prev, day: picked }));
      return;
    }

    const sameWeek = isSameWeek(start, end, { weekStartsOn: 1 }); // tuần bắt đầu từ Thứ 2
    if (!sameWeek) {
      const allDays = [...WEEK_ORDER];
      setCheckedDays(reduceToChecked(allDays));
      setFormData((prev) => ({ ...prev, day: allDays }));
      return;
    }


    const sName = weekdayFromDate(start);
    const eName = weekdayFromDate(end);
    const sIdx = WEEK_ORDER.indexOf(sName);
    const eIdx = WEEK_ORDER.indexOf(eName);

    if (sIdx === -1 || eIdx === -1) return;

    const range = WEEK_ORDER.slice(
      Math.min(sIdx, eIdx),
      Math.max(sIdx, eIdx) + 1,
    );
    setCheckedDays(reduceToChecked(range));
    setFormData((prev) => ({ ...prev, day: range }));
  }, [formData.startDate, formData.endDate, endOfRepitition]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const s = new Date(formData.startDate);
    const eDate = endOfRepitition ? new Date(formData.endDate) : null;

    if (isOutsideRosterRange(s) || (eDate && isOutsideRosterRange(eDate))) {
      toast.error(t("common.date-out-of-roster-range"));
      return;
    }
    let submitData = { ...formData };
    const selectedDaysString = Array.isArray(formData.day)
      ? formData.day.join(",")
      : Object.keys(checkedDays)
        .filter((d) => checkedDays[d])
        .join(",");
    submitData.day = selectedDaysString;

    submitData.startDate = format(submitData.startDate, "yyyy-MM-dd");
    if (endOfRepitition === true) {
      submitData.endDate = format(submitData.endDate, "yyyy-MM-dd");
    } else {
      submitData.endDate = null;
    }
    submitData.week = activeWeek + 1;
    submitData.rosterId = selectedRows.rosterId;

    submitData.day = selectedDaysString;

    if (selectedItem) {
      if (selectedItem.userId) {
        submitData.userId = selectedItem.userId;
      } else {
        submitData.groupId = selectedItem.groupId;
      }
    }

    try {
      if (page) {
        if (selectedItem.day === "Chủ Nhật") {
          submitData.day = "Sunday";
        } else if (selectedItem.day === "Thứ Hai") {
          submitData.day = "Monday";
        } else if (selectedItem.day === "Thứ Ba") {
          submitData.day = "Tuesday";
        } else if (selectedItem.day === "Thứ Tư") {
          submitData.day = "Wednesday";
        } else if (selectedItem.day === "Thứ Năm") {
          submitData.day = "Thursday";
        } else if (selectedItem.day === "Thứ Sáu") {
          submitData.day = "Friday";
        } else if (selectedItem.day === "Thứ Bảy") {
          submitData.day = "Saturday";
        } else {
          submitData.day = selectedDaysString;
        }
      } else {
        submitData.day = selectedDaysString;
      }
      submitData.langId = localStorage.getItem("i18nextLngId");
      const response = await addRosterItem(submitData);
      if (
        response.data.message === "Successful" ||
        response.data.message === "Thành công"
      ) {
        handleCloseDialog();
        toast.success(t("common.created-roster-item-successfully"));
        onRefresh();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status !== 401) {
          toast.error(t(error.response.data));
        }
      }
      console.error("There was an error creating the leave!", error);
    } finally {
      hide();
    }
  };

  const daysOfWeek = [
    { value: "Monday", label: t("common.monday") },
    { value: "Tuesday", label: t("common.tuesday") },
    { value: "Wednesday", label: t("common.wednesday") },
    { value: "Thursday", label: t("common.thursday") },
    { value: "Friday", label: t("common.friday") },
    { value: "Saturday", label: t("common.saturday") },
    { value: "Sunday", label: t("common.sunday") },
  ];

  useEffect(() => {
    if (!filteredShifts || filteredShifts.length === 0) return;

    setFormData((prev) => {
      if (prev.shift) return prev;

      return {
        ...prev,
        shift: filteredShifts[0].shiftId,
      };
    });
  }, [filteredShifts]);

  return (
    <CustomDialog maxWidth={"xl"} open onClose={handleCloseDialog} keepMounted>
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">
              {t("button.add-roster-item")}
            </p>
          </div>
          <GrayIconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div
          className="grid grid-cols-2 gap-x-10 gap-y-3 relative mt-5"
          data-tour="shift-and-roster-add-rosteritem-input"
        >
          <div className="col-span-2">
            <label className="block text-sm">
              {t("common.shift")}
              <span className="text-[#EF4444] ml-1">*</span>
            </label>
            <div className="mt-2">
              <CustomSelect
                multiple={false}
                className="min-w-60"
                name="shift"
                value={formData.shift}
                onChange={handleInputChange}
                options={filteredShifts}
                dataType="shift"
              />
            </div>
          </div>
          <div className="col-span-2 pt-2">
            <label className="block text-sm">
              {t("common.users-or-user-groups")}
              <span className="text-[#EF4444] ml-1">*</span>
            </label>
            <div className="mt-2">
              {selectedItem ? (
                <CustomInput
                  name="username"
                  inputType="text"
                  readOnly={true}
                  value={formData.username}
                  labelEnable={false}
                  placeholder={t("common.users-or-user-groups")}
                />
              ) : (
                <CustomAutocomplete
                  options={mappedUsersData}
                  freeSolo={true}
                  placeholder={t("common.users-or-user-groups")}
                  onDataChange={handleDataChange}
                  name={!formData.groupId ? "userId" : "groupId"}
                  dataType="userOrGroup"
                  resetTrigger={resetTrigger.userId}
                  value={selectedValue.userId || selectedValue.groupId}
                />
              )}
            </div>
          </div>
          {/* {page ?} */}
          {!page && (
            <>
              <div className="col-span-2 pt-2">
                <label className="block text-sm">
                  {t("common.week-days")}
                  <span className="text-[#EF4444] ml-1">*</span>
                </label>
                <div className="pt-2">
                  <div>
                    <FormGroup row>
                      {daysOfWeek.map((day, index) => (
                        <FormControlLabel
                          key={index}
                          sx={{
                            mr: "10px",
                            height: "30px",
                            alignItems: "center",
                          }}
                          control={
                            <Checkbox
                              value={day.value}
                              checked={checkedDays[day.value]}
                              onChange={handleCheckDays}
                              name="day"
                            />
                          }
                          label={day.label}
                        />
                      ))}
                    </FormGroup>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="col-span-2 mt-2">
            <div className="">
              <label className="block text-sm">
                {t("common.start-date")}
                <span className="text-[#EF4444] ml-1">*</span>
              </label>
              <div className="mt-2">
                <CustomDateTimePicker
                  mode="single"
                  value={formData.startDate}
                  minDate={rosterStartDate}
                  maxDate={rosterEndDate ?? undefined}
                  onChange={(newValue) =>
                    handleInputChange({
                      target: {
                        name: "startDate",
                        value: newValue,
                      },
                    })
                  }
                  disableTime
                  showOnlyDate
                />
              </div>
            </div>
            <div className="pt-0">
               <label className="block text-sm mt-2">
                  {t('common.end-of-repetition')}
               </label>
               <FormControl component="fieldset" className="w-full mt-2">
                  <RadioGroup
                     aria-label="End of repitition"
                     name="endOfRepitition"
                     value={endOfRepitition}
                     onChange={handleChangeEndOfRepitition}
                     className="!grid grid-cols-1 md:grid-cols-2 gap-4 w-full !m-0"
                  >
                     <FormControlLabel
                        className="!m-0 items-center"
                        value={false}
                        control={<Radio size="small" />}
                        label={t('common.no-end-date')}
                     />
                     <div className="flex gap-2">
                        <FormControlLabel
                           className="!m-0 items-center"
                           value={true}
                           control={<Radio size="small" />}
                           label={t('common.end-by')}
                        />
                        <div className="w-[270px] max-w-full">
                           <CustomDateTimePicker
                              mode="single"
                              value={formData.endDate ?? formData.startDate}
                              minDate={rosterStartDate}
                              maxDate={rosterEndDate ?? undefined}
                              disabled={!endOfRepitition}
                              onChange={(newValue) =>
                                 handleInputChange({
                                    target: {
                                       name: 'endDate',
                                       value: newValue,
                                    },
                                 })
                              }
                              disableTime
                              showOnlyDate
                           />
                        </div>
                     </div>
                  </RadioGroup>
               </FormControl>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={handleCloseDialog}>
            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={handleSubmit}
            disabled={
              !formData.shift ||
              !formData.startDate ||
              !(
                selectedItem?.userId ||
                selectedItem?.groupId ||
                formData?.userId ||
                formData?.groupId
              ) ||
              !Object.values(checkedDays).some(Boolean)
            }
          >
            <p>{t("button.add")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default AddRosterItemsDialog;
